import type {
  DailySalesDay,
  DailySalesReport,
  SalesDateRange,
  SalesTotals,
} from '../../../api/daily-sales';
import { formatVenueDate } from '../../../utils/venueTime';
import type { KpiCell } from '../primitives';

import { daysIn } from './daily-sales-range';

export type SourceKey =
  | 'fareharbor'
  | 'giftCards'
  | 'nebari'
  | 'giftPasses'
  | 'retailInStore'
  | 'retailOnline';
export type Measure = keyof SalesTotals;

export const SOURCES: ReadonlyArray<{ key: SourceKey; name: string; color: string }> = [
  { key: 'fareharbor', name: 'FareHarbor', color: 'var(--nb-info)' },
  { key: 'giftCards', name: 'FareHarbor gift cards', color: 'var(--nb-gold)' },
  { key: 'nebari', name: 'Nebari', color: 'var(--nb-primary)' },
  { key: 'giftPasses', name: 'Nebari gift passes', color: 'var(--nb-ds-gift-pass)' },
  { key: 'retailInStore', name: 'Retail in store', color: 'var(--nb-ds-retail-store)' },
  { key: 'retailOnline', name: 'Retail online', color: 'var(--nb-ds-retail-online)' },
];

export const ALL_SOURCES: SourceKey[] = SOURCES.map((s) => s.key);

export const ZERO: SalesTotals = { bookings: 0, tickets: 0, grossCents: 0, subtotalCents: 0 };

export function addTotals(a: SalesTotals, b: SalesTotals): SalesTotals {
  return {
    bookings: a.bookings + b.bookings,
    tickets: a.tickets + b.tickets,
    grossCents: a.grossCents + b.grossCents,
    subtotalCents: a.subtotalCents + b.subtotalCents,
  };
}

/** A source's totals for the day, as zeros when the source could not be read. */
export function totalsOf(day: DailySalesDay, source: SourceKey): SalesTotals {
  return day[source] ?? ZERO;
}

export function dayTotal(day: DailySalesDay, measure: Measure, sources = ALL_SOURCES): number {
  return sources.reduce((sum, s) => sum + totalsOf(day, s)[measure], 0);
}

export function windowTotal(days: DailySalesDay[], source: SourceKey, measure: Measure): number {
  return days.reduce((sum, d) => sum + totalsOf(d, source)[measure], 0);
}

/** The whole window for the given sources, added together. */
export function windowSum(days: DailySalesDay[], measure: Measure, sources = ALL_SOURCES): number {
  return sources.reduce((sum, s) => sum + windowTotal(days, s, measure), 0);
}

/** One row per day with a column per source, the shape a stacked bar chart reads. */
export function chartRows(days: DailySalesDay[], sources: SourceKey[], measure: Measure) {
  return days.map((d) => ({
    date: d.date,
    ...Object.fromEntries(sources.map((s) => [s, totalsOf(d, s)[measure]])),
  }));
}

export function formatMoney(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

/** An axis label in thousands: `$2.5K`, `$10K`. */
export function formatMoneyAxis(cents: number): string {
  const thousands = cents / 100_000;
  if (thousands === 0) return '$0';
  return `$${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}K`;
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** `Oct 8` from `2026-10-08`. Calendar dates carry no time zone, so none is applied. */
export function shortDate(date: string): string {
  return formatVenueDate(date, 'MMM d');
}

export function weekdayName(date: string, length: 'short' | 'long' = 'short'): string {
  return formatVenueDate(date, length === 'short' ? 'EEE' : 'EEEE');
}

export function longDate(date: string): string {
  return formatVenueDate(date, 'MMM d, yyyy');
}

/** A moment, as staff in the head office read it: `Oct 9, 1:48 PM ET`. */
export function easternTime(iso: string): string {
  const text = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
  return `${text} ET`;
}

function change(current: number, previous: number): Pick<KpiCell, 'delta' | 'good'> {
  if (!previous) return {};
  const pct = Math.round(((current - previous) / previous) * 100);
  return { delta: `${pct >= 0 ? '+' : '−'}${Math.abs(pct)}%`, good: pct >= 0 };
}

/** The share of ticket bookings Nebari took, from its first sale in the window. */
export function nebariShare(days: DailySalesDay[]): { pct: number; since: string } | null {
  const first = days.findIndex((d) => d.nebari.bookings > 0);
  if (first < 0) return null;
  const since = days.slice(first);
  const nebari = windowTotal(since, 'nebari', 'bookings');
  const all = windowSum(since, 'bookings', ['fareharbor', 'nebari']);
  return { pct: (nebari / all) * 100, since: days[first].date };
}

export function rangeLabel(range: SalesDateRange): string {
  const days = daysIn(range);
  return `${shortDate(range.from)} – ${longDate(range.to)} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** What a count of the sources is called: bookings for tickets and gift cards, orders
 *  for retail. */
const ORDER_SOURCES: SourceKey[] = ['giftPasses', 'retailInStore', 'retailOnline'];

export function countNoun(sources: SourceKey[]): string {
  const orders = sources.filter((s) => ORDER_SOURCES.includes(s)).length;
  if (orders === 0) return 'bookings';
  return orders === sources.length ? 'orders' : 'bookings & orders';
}

/** The summary day against the same weekday a week before. It is yesterday while the
 *  range runs to today, and is then named that way; otherwise it is named by its date. */
function kpiDayCells(report: DailySalesReport, sources: SourceKey[]): KpiCell[] {
  const day = report.days.find((d) => d.date === report.kpiDay);
  if (!day) return [];
  const weekBefore = report.kpiWeekBefore;
  const name =
    report.to === report.today ? 'Yesterday' : `${weekdayName(day.date)} ${shortDate(day.date)}`;
  const vs = weekBefore ? `vs ${weekdayName(weekBefore.date)} ${shortDate(weekBefore.date)}` : '';
  const bookings = dayTotal(day, 'bookings', sources);
  const gross = dayTotal(day, 'grossCents', sources);
  return [
    {
      label: `${name} · ${countNoun(sources)}`,
      value: formatCount(bookings),
      ...(weekBefore ? change(bookings, dayTotal(weekBefore, 'bookings', sources)) : {}),
      sub: weekBefore ? vs : weekdayName(day.date, 'long'),
    },
    {
      label: `${name} · gross payment`,
      value: formatMoney(gross),
      ...(weekBefore ? change(gross, dayTotal(weekBefore, 'grossCents', sources)) : {}),
      sub: vs || undefined,
    },
  ];
}

/** What the window's units add up to, one phrase per kind of thing sold. */
function unitsSold(days: DailySalesDay[], sources: SourceKey[]): string {
  const parts: Array<[SourceKey[], string]> = [
    [['fareharbor', 'nebari'], 'tickets'],
    [['giftCards'], 'gift cards'],
    [['giftPasses'], 'gift passes'],
    [['retailInStore', 'retailOnline'], 'retail items'],
  ];
  return parts
    .map(([group, noun]) => [group.filter((s) => sources.includes(s)), noun] as const)
    .filter(([group]) => group.length > 0)
    .map(([group, noun]) => `${formatCount(windowSum(days, 'tickets', group))} ${noun}`)
    .join(' · ');
}

/** The summary day, then the whole window, over the sources on show. */
export function summaryCells(
  report: DailySalesReport,
  sources: SourceKey[] = ALL_SOURCES,
): KpiCell[] {
  const { days } = report;
  const cells: KpiCell[] = kpiDayCells(report, sources);

  cells.push(
    {
      label: `${days.length} days · ${countNoun(sources)}`,
      value: formatCount(windowSum(days, 'bookings', sources)),
      sub: unitsSold(days, sources),
    },
    {
      label: `${days.length} days · gross payment`,
      value: formatMoney(windowSum(days, 'grossCents', sources)),
      sub: `${formatMoney(windowSum(days, 'subtotalCents', sources))} pre-tax`,
    },
  );

  const share = sources.includes('nebari') ? nebariShare(days) : null;
  if (share) {
    cells.push({
      label: 'Nebari share of ticket bookings',
      value: `${share.pct.toFixed(1)}%`,
      sub: `since ${shortDate(share.since)}`,
    });
  }
  return cells;
}
